import { Grid, Tooltip, Typography } from '@material-ui/core';
import React from 'react';
import Editor from 'react-simple-code-editor';

import { getMacrosWiki, getResellerTitle } from '~/src/dash/resellers/utils/importResellerConfig';
import { extractURLDomain, findDCMParams } from '~/src/dash/resellers/utils/utils';

interface IProps {
  url: string;
  urlID: number;
  highlight: (code: any) => any;
  padding: number;
  maxLength: number;
  onURLChange: (text: string) => any;
  textStyle?: object;
  wikiMsgHidden?: boolean;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  edit?: boolean;
  brandSafety?: boolean;
  disableSiteIDMacro?: boolean;
  sendErrorsToParent?: (action: string, urlsWithErrors: number) => void;
}

interface IState {
  url: string;
  warning: boolean;
  replaceWarning: boolean;
  warningMsg: string;
}

export const publicaMacroPairs: { [key: string]: string } = {
  ad_duration: '[AD_DURATION]',
  ad_break_id: '[AD_BREAK_ID]',
  app_bundle: '[APP_BUNDLE]',
  app_domain: '[APP_DOMAIN]',
  app_name: '[%APP_NAME%]',
  app_store_url: '[%APP_STORE_URL%]',
  ba_cb: '[CACHEBUSTER]',
  buyeruid: '[BUYERUID]',
  cachebuster: '[CACHEBUSTER]',
  cb: '[CACHEBUSTER]',
  channel_label_id: '[CHANNEL_LABEL_ID]',
  consent: '[CONSENT]',
  content_cat: '[CONTENT_CAT]',
  content_channel: '[CONTENT_CHANNEL]',
  content_context: '[CONTENT_CONTEXT]',
  content_dist_id: '[CONTENT_DIST_ID]',
  content_dist_name: '[CONTENT_DIST_NAME]',
  content_episode: '[CONTENT_EPISODE]',
  content_genre: '[%CONTENT_GENRE%]',
  content_id: '[CONTENT_ID]',
  content_keywords: '[CONTENT_KEYWORDS]',
  content_language: '[CONTENT_LANGUAGE]',
  content_length: '[CONTENT_LENGTH]',
  content_network: '[CONTENT_NETWORK]',
  content_prodqual: '[CONTENT_PROD]',
  content_producer_id: '[CONTENT_PRODUCER_ID]',
  content_producer: '[CONTENT_PRODUCER_NAME]',
  content_rating: '[CONTENT_RATING]',
  content_season: '[CONTENT_SEASON]',
  content_series: '[%CONTENT_SERIES%]',
  content_title: '[%CONTENT_TITLE%]',
  content_url: '[CONTENT_URL]',
  coppa: '[COPPA]',
  country: '[COUNTRY]',
  custom_5: '[CUSTOM_5]',
  custom_6: '[CUSTOM_6]',
  custom_7: '[CUSTOM_7]',
  custom_8: '[CUSTOM_8]',
  custom_targeting: '[CUSTOM_TARGETING]',
  dc_lat: '[DNT]',
  dc_rdid: '[DID]',
  demand_source: '[DEMAND_SOURCE]',
  did: '[DID]',
  device_language: '[DEVICE_LANGUAGE]',
  device_make: '[%DEVICE_MAKE%]',
  device_model: '[%DEVICE_MODEL%]',
  device_os: '[DEVICE_OS]',
  deviceosversion: '[DEVICEOSVERSION]',
  device_os_version: '[DEVICE_OS_VERSION]',
  device_type: '[%DEVICE_TYPE%]',
  dma_code: '[DMA_CODE]',
  dnt: '[DNT]',
  er_did: '[DID]',
  gdpr: '[GDPR]',
  gdpr_consent: '[CONSENT]',
  gpp: '[GPP]',
  gpp_sid: '[GPP_SID]',
  gender: '[GENDER]',
  genre_id: '[GENRE_ID]',
  house_ads: '[HOUSE_ADS]',
  ifa_type: '[IFA_TYPE]',
  ip: '[%IP%]',
  iris_id: '[IRIS_ID]',
  iris_context: '[IRIS_CONTEXT]',
  is_lat: '[LIMITED_AD_TRACKING]',
  livestream: '[LIVESTREAM]',
  max_ad_duration: '[MAX_AD_DURATION]',
  max_bitrate: '[MAXBITRATE]',
  min_ad_duration: '[MIN_AD_DURATION]',
  min_bitrate: '[MINBITRATE]',
  ord: '[TIMESTAMP]',
  player_height: '[HEIGHT]',
  player_width: '[WIDTH]',
  pod_duration: '[POD_DURATION]',
  position: '[POSITION]',
  preferred_language: '[PREFERRED_LANGUAGE]',
  rand: '[CACHEBUSTER]',
  rights_holder: '[RIGHTS_HOLDER]',
  roku_ad_keys: '[ROKU_AD_KEYS]',
  roku_ad_query_id: '[ROKU_AD_QUERY_ID]',
  schain: '[SCHAIN]',
  session_id: '[SESSION_ID]',
  site_id: '[SITE_ID]',
  site_name: '[SITE_NAME]',
  site_page: '[%PAGE_URL%]',
  slot_count: '[SLOT_COUNT]',
  ssai_enabled: '[SSAI_ENABLED]',
  ssai_vendor: '[SSAI_VENDOR]',
  tag_for_child_directed_treatment: '[COPPA]',
  targeted_device: '[TARGETED_DEVICE]',
  time_offset: '[TIME_OFFSET]',
  timestamp: '[TIMESTAMP]',
  trackeruuid: '[TRACKERUUID]',
  ua: '[%UA%]',
  userage: '[USERAGE]',
  us_privacy: '[US_PRIVACY]',
  vmap_pod_duration: '[VMAP_POD_DURATION]',
  yob: '[YOB]',
  venueid: '[VENUEID]',
  venuetypeid: '[VENUETYPEID]',
  impmultiply: '[IMPMULTIPLY]',
  positiontype: '[POSITIONTYPE]',
};

// Regex to check URL
// https://regex101.com/r/QFpuKl/1
// https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
// example: https://rtr.innovid.com/r1.5f764879f14255.99127415;cb=[CACHEBUSTER]
// example: https://ad.doubleclick.net/ddm/pfadx/N266802.127425PANDORA0/B23506139.266342556;sz=0x0;ord=1409971816;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;dcmt=text/xml;dc_sdk_apis=7;dc_omid_p=Pandora/2005.2;dc_vast=3
// example: http://unified.adsafeprotected.com/vast/fwjsvid/st/486790/47940079/skeleton.js?includeFlash=false&originalVast=https://ad.doubleclick.net/ddm/pfadx/N7584.1916649VIDEOAMP/B24547163.280300362;sz=0x0;ord=1666882855;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;dcmt=text/xml;dc_sdk_apis=[APIFRAMEWORKS];dc_omid_p=[OMIDPARTNER]
// example: https://ssp.api.tappx.com/vasttag/v1/VAST35098CA?key=pub-55355-android-5666&os=[DEVICE_OS]&ip=[IP]&source=app&ab=[APP_BUNDLE]&vpos=1&vw=[WIDTH]&vh=[HEIGHT]&vmin=15&vmax=90&vfmt=1&an=[%APP_NAME%]&url=[%APP_STORE_URL%]&aid=[DID]&cb=[CACHEBUSTER]&us_privacy=[US_PRIVACY]&aidl=[DNT]&ua=[%UA%]&vvers=2,3&acat=entertainment&vpaid=false&vplay=0&secure=1
// example: http://ads.stickyadstv.com/www/delivery/swfIndex.php?reqType=AdsSetup&protocolVersion=2.0&zoneId=9230929&ip=[IP]&userAgent=[%UA%]&deviceIfa=[DID]&appBundle=[APP_BUNDLE]&appName=philo&appStoreUrl=[%APP_STORE_URL%]&playerSize=1920x1080&appVersion=&schain=1.0,1!vitor.media,1027313,1,,,,
export const checkURL = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?' + // port
    '(\\/.*$)',
  'i', // path and query
);

export const checkURLProtocolRequired = new RegExp(
  '^(https?:\\/\\/)' + // protocol REQUIRED (protocol is required when using the js URL type)
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+;,=\\[\\]]*)*' + // port and path
    '(\\?[-;:&a-zA-Z\\d%_.~+=\\[\\]\\{\\}\\/\\(\\),!$]*)?' + // query string
    '(\\#[-;:a-z\\d_.\\/]*)?$',
  'i',
);

export const isValidUrl = (urlString: string) => {
  const urlPattern = new RegExp(
    '^(https?:\\/\\/)?' + // validate protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
};

const isDCMTag = (tag: string): boolean => extractURLDomain(tag).includes('doubleclick.net');

export class VastEditor extends React.Component<IProps, IState> {
  // State initialization
  state = {
    url: this.props.url,
    warning: false,
    replaceWarning: false,
    warningMsg: '',
  };

  // Constructor
  constructor(props: IProps) {
    super(props);
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.props.url !== prevProps.url) {
      this.handleURLChange(this.props.url);
    }
  }
  // Executed at each VAST input value change
  handleURLChange = (text: string) => {
    if (!this.props.disabled) {
      this.setState(
        {
          url: text,
        },
        () => {
          const textIsDCMTag = isDCMTag(text);
          if (this.checkVastTagValidity(text, textIsDCMTag)) {
            this.checkMacroValidity(text, textIsDCMTag);
          }
          this.props.onURLChange(text);
        },
      );
    }
  };

  // Check if VAST tag is a valid URL
  checkVastTagValidity = (text: string, isDCMTag: boolean) => {
    let warningMsg = '';
    if (!checkURLProtocolRequired.test(text) && !isDCMTag) {
      warningMsg = `Vast URLs must be a valid URL or dcm tag. Valid URLs must contain ${
        this.props.brandSafety ? 'https://' : 'protocol'
      }, domain name or ip and port / path`;
      this.setState({
        warning: true,
        replaceWarning: false,
        warningMsg: `WARNING: This is not a valid URL. ${warningMsg}`,
      });
    } else if (this.props.brandSafety && text.slice(0, 8).toLocaleLowerCase() !== 'https://') {
      warningMsg = 'URLs must start with "https://"';
      this.setState({
        warning: true,
        replaceWarning: false,
        warningMsg: `WARNING: This is not a valid URL. ${warningMsg}`,
      });
    }
    if (warningMsg !== '') {
      if (this.props.sendErrorsToParent) {
        this.props.sendErrorsToParent('add', this.props.urlID);
      }
      return false;
    }
    if (this.props.sendErrorsToParent) {
      this.props.sendErrorsToParent('remove', this.props.urlID);
    }
    this.setState({
      warning: false,
      replaceWarning: false,
      warningMsg: '',
    });
    return true;
  };

  // Return style for text input field
  getTextStyle = () => {
    const style = this.props.textStyle !== undefined ? this.props.textStyle : {};
    if (this.state.warning && !this.state.replaceWarning) {
      // Error style
      return { ...style, border: 'solid rgb(255, 165, 0, 0.05) 1px', background: '#fff5f5' };
    }
    // Normal style
    return style;
  };

  // Rendering
  render() {
    return (
      <Grid style={{ width: '100%' }}>
        {/* VAST text editor */}
        <Grid style={this.getTextStyle()}>
          <Editor
            placeholder={this.props.placeholder}
            value={this.state.url}
            highlight={this.props.highlight}
            padding={this.props.padding}
            maxLength={this.props.maxLength}
            onValueChange={this.handleURLChange}
            style={this.props.style}
          />
        </Grid>
        {/* Error message */}
        {this.state.warning && (
          <Typography
            style={{
              color: 'orange',
              marginTop: '0px',
              marginBottom: '0px',
              fontWeight: 600,
              fontSize: '12px',
            }}
          >
            {this.state.warningMsg}
          </Typography>
        )}
        {this.state.replaceWarning && (
          <Grid
            style={{
              backgroundColor: 'rgba(242, 174, 73, .1)',
              display: 'flex',
              marginTop: '5px',
              marginBottom: '0px',
              padding: '5px 10px',
            }}
          >
            <Typography
              style={{
                fontSize: '12px',
                marginRight: '10px',
              }}
            >
              We've noticed query params that could be replaced with {getResellerTitle()} macros.
            </Typography>
            <Tooltip
              title={
                'Replace all available macros with ' +
                getResellerTitle() +
                ' macros. You may go back and manually override select params as desired.'
              }
            >
              <Typography
                style={{
                  color: '#5D66FA',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  this.replaceMacros(this.state.url);
                }}
              >
                [ ] Replace
              </Typography>
            </Tooltip>
          </Grid>
        )}
        {/* Wiki message */}
        {!this.props.wikiMsgHidden && (
          <Typography
            style={{ marginTop: '0px', color: '#636363', fontSize: '12px', paddingLeft: '10px' }}
          >
            For supported macros, please refer to our&nbsp;
            {/* NOTE: add rel='noopener' since _blank is a potential security issue https://web.dev/external-anchors-use-rel-noopener/ */}
            <a href={getMacrosWiki()} target='_blank' rel='noopener'>
              wiki
            </a>
          </Typography>
        )}
      </Grid>
    );
  }

  // Checking macro validity
  checkMacroValidity(url: string, isDCMTag: boolean) {
    let searchParams: URLSearchParams;
    if (isDCMTag) {
      searchParams = findDCMParams(url);
    } else {
      // url must be encoded so that searchParams will respect macros with [% %]
      searchParams = new URL(encodeURI(url)).searchParams;
    }

    for (const [param, value] of searchParams) {
      if (param === 'site_id' && this.props.disableSiteIDMacro) {
        continue;
      }
      if (value !== '' && param in publicaMacroPairs && publicaMacroPairs[param] !== value) {
        this.setState({
          warning: false,
          replaceWarning: true,
          warningMsg: 'WARNING: URL contains potential ' + getResellerTitle() + ' macros.',
        });
        return;
      }
    }

    this.setState({
      warning: false,
      replaceWarning: false,
      warningMsg: '',
    });
  }

  replaceMacros(urlString: string) {
    if (isDCMTag(urlString)) {
      this.replaceDCMTagMacros(urlString);
    } else {
      this.replaceURLMacros(urlString);
    }
  }

  // Replace macros with their values
  replaceMacrosInSearchParams(searchParams: URLSearchParams) {
    for (const [param] of searchParams) {
      if (param in publicaMacroPairs) {
        if (param === 'site_id' && this.props.disableSiteIDMacro) {
          continue;
        }
        searchParams.set(param, publicaMacroPairs[param]);
      }
    }
  }

  replaceURLMacros(urlString: string) {
    const url = new URL(urlString);
    const searchParams = url.searchParams;

    this.replaceMacrosInSearchParams(searchParams);

    // url must be decoded so that we can display the correct string characters such as [% %]
    const newUrl = decodeURI(url.toString());
    this.setState({
      url: newUrl,
      warning: false,
      replaceWarning: false,
      warningMsg: '',
    });
    this.props.onURLChange(newUrl);
  }

  replaceDCMTagMacros(dcnTagString: string) {
    const searchParams = findDCMParams(dcnTagString);
    this.replaceMacrosInSearchParams(searchParams);

    const decodedParams = decodeURI(searchParams.toString());
    const newURL = dcnTagString.split(';')[0] + ';' + decodedParams;
    // @ts-ignore
    const newTag = newURL.replaceAll('&', ';');

    this.setState({
      url: newTag,
      warning: false,
      replaceWarning: false,
      warningMsg: '',
    });
    this.props.onURLChange(newTag);
  }
}
